import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Hero from './features/hero/Hero';
import About from './features/about/About';
import Projects from './features/projects/Projects';
import Experience from './features/experience/Experience';
import Achievements from './features/achievements/Achievements';
import MainPage from './pages/MainPage';
import ContactPage from './pages/ContactPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ThankYouPage from './pages/ThankYouPage';
import NotFoundPage from './pages/NotFoundPage';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import GlobalCrosshairCursor from './components/common/GlobalCrosshairCursor';
import { useActiveSection } from './hooks/useActiveSection';
import { trackPageView } from './utils/analytics';
import './styles/fullpage.css';

// * Section registry defining landing page component mapping and order (5 core sections)
const SECTIONS = [
  { id: 'home', Component: Hero },
  { id: 'about', Component: About },
  { id: 'projects', Component: Projects },
  { id: 'experience', Component: Experience },
  { id: 'achievements', Component: Achievements },
];

const SECTION_IDS = SECTIONS.map((s) => s.id);

function App() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  const location = useLocation();

  // * Active section tracking via RAF scroll spy once DOM is mounted
  // ? Completely removes scroll-jacking; native browser handles all smooth scrolling
  const { activeIndex, scrollToSection } = useActiveSection({
    sectionIds: SECTION_IDS,
    enabled: location.pathname === '/',
  });

  // * Track route changes in analytics
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  // * Responsive breakpoint listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // * Intercept anchor clicks to trigger native smooth scrolling on home page
  const handleNavClick = useCallback(
    (e: MouseEvent) => {
      if (location.pathname !== '/') return;

      const target = (e.target as HTMLElement)?.closest('a[href^="#"]');
      if (!target) return;

      const href = target.getAttribute('href');
      if (!href || href === '#') return;

      const sectionId = href.replace('#', '');
      e.preventDefault();
      scrollToSection(sectionId);
    },
    [location.pathname, scrollToSection]
  );

  // * Handle cross-page anchor routing (e.g. from /contact to /#about)
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const sectionId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash, scrollToSection]);

  // * Custom navigation event listener for internal section jumps
  useEffect(() => {
    document.addEventListener('click', handleNavClick, true);

    const handleCustomNavigate = (e: Event) => {
      const targetId = (e as CustomEvent)?.detail?.targetId;
      if (targetId) {
        scrollToSection(targetId);
      }
    };
    window.addEventListener('navigate-section', handleCustomNavigate);

    return () => {
      document.removeEventListener('click', handleNavClick, true);
      window.removeEventListener('navigate-section', handleCustomNavigate);
    };
  }, [handleNavClick, scrollToSection]);

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <MainPage
              isMobile={isMobile}
              currentSection={activeIndex}
              sections={SECTIONS}
            />
          }
        />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <CookieConsentBanner />
      <GlobalCrosshairCursor />
    </>
  );
}

export default App;